import { GetServerSideProps } from 'next';
import React, { lazy, Suspense } from 'react';
import { lazily } from 'react-lazily';
import { z } from 'zod';

import { getByUrl } from 'services/freeform';
import { HeadObject } from 'services/generic';
import {
	getArticlesBySection,
	getCategories,
	getSectionsByCategory,
	zenDeskCategorySchema,
} from 'services/helpcentre';

import { useBrowsing } from 'hooks/use-browsing';
import { ReplaceCrumb, useSetBreadcrumbs } from 'hooks/use-set-breadcrumbs';

import { Seo } from 'core/mancity/components/general/seo';
import type { FreeFormBody } from 'core/mancity/template/freeform';

import { addFreeformControlData } from '@help-centre/helpers';

import styles from '../../index.module.scss';

const Breadcrumbs = lazy(
	() => import('core/generic/components/navigation/breadcrumbs')
);
const { FreeformPage } = lazily(() => import('core/mancity/template/freeform'));

type SectionByIdPropsType = {
	body: FreeFormBody;
	currentCategory: z.infer<typeof zenDeskCategorySchema>;
	metaData: HeadObject;
};

export default function SectionById({
	body,
	currentCategory,
	metaData,
}: SectionByIdPropsType) {
	const router = useBrowsing();
	const { generateSpecificBreadcrumbs } = useSetBreadcrumbs();

	const titleSuffix = ' | Man City Help Centre'; // @totdo: get this from translation dictionary
	metaData.title = currentCategory.name + titleSuffix;

	const breadcrubmsReplace: Map<string, ReplaceCrumb> = new Map([
		['/sections', { isHidden: true }],
		[router.asPath.split('?')[0], { title: currentCategory.name }],
	]);

	const specificBreadcrumbs = generateSpecificBreadcrumbs({
		baseName: 'Help & Support',
		replace: breadcrubmsReplace,
	});

	return (
		<>
			{metaData && <Seo head={metaData} />}
			<Suspense fallback={null}>
				<div>
					<div className="row">
						<div className="col-12">
							<div className={styles.breadcrumbs}>
								<Breadcrumbs listCrumbs={specificBreadcrumbs} />
							</div>
							<FreeformPage body={body} />
						</div>
					</div>
				</div>
			</Suspense>
		</>
	);
}

export const getServerSideProps: GetServerSideProps = async (context) => {
	const { id } = context.params as { id: string };
	const slugParts = id.split('-');
	const slugId = slugParts[slugParts.length - 1] || id;
	let metaData = null;

	const sectionsByCategory = await getSectionsByCategory(+slugId)
		.then((response) => response)
		.catch(() => []);

	if (!sectionsByCategory.length) {
		// If endpoint returns 404 OR 500 OR invalid, show Next.js 404 page
		return { notFound: true };
	}

	const categories = await getCategories().then((response) => response);
	const currentCategory = categories.filter(
		(category) => category.zenDeskId == +slugId
	)[0];
	if (!currentCategory) {
		return { notFound: true };
	}

	const updatedSectionsByCategory = await Promise.all(
		sectionsByCategory.map(async (section) => {
			try {
				const sectionArticles = await getArticlesBySection(
					section.zenDeskId
				);
				return { ...section, sectionArticles };
			} catch {
				return { ...section };
			}
		})
	);

	const body = await getByUrl('/help/sections', 'en', 'gb')
		.then((response) => {
			metaData = { ...response.head };
			return addFreeformControlData(
				response,
				'ZenDeskSectionsByCategory',
				updatedSectionsByCategory,
				currentCategory.name
			);
		})
		.catch(() => null);
	if (!body || !metaData) {
		return { notFound: true };
	}

	return {
		props: { body, currentCategory, metaData },
	};
};
